import React, { useState, useEffect } from 'react';
import Modal, { ModalDirectionOptions } from '../components/Modal';
import './AnionGapModal.scss';
import { useTranslation } from 'react-i18next';

interface AnionGapInputs {
  sodium: string;
  chloride: string;
  bicarbonate: string;
}

interface AnionGapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (gap: number) => void;
}

const AnionGapModal: React.FC<AnionGapModalProps> = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslation(['resus']);
  const [inputs, setInputs] = useState<AnionGapInputs>({
    sodium: '',
    chloride: '',
    bicarbonate: ''
  });
  
  const [anionGapResult, setAnionGapResult] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setInputs({
        sodium: '',
        chloride: '',
        bicarbonate: ''
      });
      setAnionGapResult(null);
    }
  }, [isOpen]);

  useEffect(() => {
    // Calculate anion gap on input change
    calculateAnionGap();
  }, [inputs]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs({
      ...inputs,
      [name]: value
    });
  };

  const calculateAnionGap = () => {
    const sodium = parseFloat(inputs.sodium);
    const chloride = parseFloat(inputs.chloride);
    const bicarbonate = parseFloat(inputs.bicarbonate);

    if (!isNaN(sodium) && !isNaN(chloride) && !isNaN(bicarbonate)) {
      const gap = sodium - (chloride + bicarbonate);
      setAnionGapResult(gap);
      return gap;
    }
    
    setAnionGapResult(null);
    return null;
  };

  const getAnionGapInterpretation = (gap: number) => {
    if (gap < 8) {
      return "Low (normal range: 8-11)";
    } else if (gap >= 8 && gap < 12) {
      return "Normal range (8-11)";
    } else {
      return "Elevated (normal range: 8-11)";
    }
  };

  const handleConfirm = () => {
    if (anionGapResult !== null) {
      onSave(anionGapResult);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      title={t('secondarySurvey.anionGapResult')}
      direction={ModalDirectionOptions.LTR}
      primaryButton={{
        text: t('buttons.update'),
        onClick: handleConfirm
      }}
      secondaryButton={{
        text: t('anionGapModal.closeButton'),
        onClick: onClose
      }}
    >
      <div className="anion-gap-content">
        <div className="input-container">
          <div className="form-group">
            <label htmlFor="sodium">Sodium (Na+):</label>
            <input 
              type="number" 
              className="form-control" 
              id="sodium" 
              name="sodium" 
              value={inputs.sodium} 
              onChange={handleInputChange} 
              placeholder="mEq/L"
              data-testid="secondary-survey-anion-gap-sodium"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="chloride">Chloride (Cl-):</label>
            <input 
              type="number" 
              className="form-control" 
              id="chloride" 
              name="chloride" 
              value={inputs.chloride} 
              onChange={handleInputChange}
              placeholder="mEq/L"
              data-testid="secondary-survey-anion-gap-chloride"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="bicarbonate">Bicarbonate (HCO3-):</label>
            <input 
              type="number" 
              className="form-control" 
              id="bicarbonate" 
              name="bicarbonate" 
              value={inputs.bicarbonate} 
              onChange={handleInputChange}
              placeholder="mEq/L"
              data-testid="secondary-survey-anion-gap-bicarbonate"
            />
          </div>
        </div>
        
        {anionGapResult !== null && (
          <div className="anion-gap-result">
            <p className="interpretation">Anion Gap: {anionGapResult.toFixed(1)}</p>
            <p className="explanation">{getAnionGapInterpretation(anionGapResult)}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AnionGapModal;
